import { useState, useEffect } from 'react';
import { Collapse, Flex, Typography, Input, Button, Space, Segmented, Tooltip, Alert, Popconfirm, message } from 'antd';
import { CopyOutlined, ThunderboltOutlined, StopOutlined, LockOutlined, GlobalOutlined, UserDeleteOutlined, ClearOutlined } from '@ant-design/icons';

const { Text } = Typography;

// No 0/O or 1/I/L, so a passcode read out loud or off a slide survives.
const ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

function generatePasscode(length = 6) {
    const bytes = new Uint32Array(length);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, b => ALPHABET[b % ALPHABET.length]).join('');
}

const MODES = {
    off: {
        label: 'Off',
        icon: <StopOutlined />,
        text: 'Only members can take part. The guest link says guest joining is closed.',
    },
    passcode: {
        label: 'With passcode',
        icon: <LockOutlined />,
        text: 'Anyone with the link and the passcode can join as a guest, without an account.',
    },
    public: {
        label: 'Public',
        icon: <GlobalOutlined />,
        text: 'Anyone with the link can join as a guest, without an account — no passcode.',
    },
};

/**
 * Guest access, in the admin view: whether people without an account may
 * join the Gathering at /gathering/join — nobody, anyone with the passcode
 * (like a meeting PIN), or anyone with the link — and the link to share.
 * Guests only ever see the Gathering, are tagged as guests everywhere they
 * show up, and expire after a day. The passcode is kept while another mode
 * is chosen, so switching guests off and on again keeps the same code.
 * Also who's here as a guest, and the clean-up: expired guests are refused
 * already but linger (picks included) until the next guest joins, so the
 * admin can remove them now — or end every guest pass at once.
 * Only rendered when the node has feature_gathering_guest_access on.
 * Collapsed by default — it's set up once, not run — with the mode and the
 * guest count in the header so it still reads at a glance.
 */
function GuestAccess({ mode = 'off', passcode, onChange, guests, onRemoveGuests, sample }) {
    const [draft, setDraft] = useState(passcode || '');
    useEffect(() => setDraft(passcode || ''), [passcode]);

    const link = `${window.location.origin}/gathering/join`;
    const disabled = sample ? 'Sample data is only visible to you — turn it off to set up guest access' : null;

    // Choosing "With passcode" with no code yet makes one up, so the mode is
    // never left without a way in.
    const chooseMode = next => onChange(next === 'passcode' && !passcode
        ? { guest_mode: next, guest_passcode: generatePasscode() }
        : { guest_mode: next });
    const commitPasscode = () => {
        const next = draft.trim();
        if (next === (passcode || '')) return;
        if (!next) { setDraft(passcode || ''); message.info('Guests need a passcode in this mode — choose Off or Public instead'); return; }
        onChange({ guest_passcode: next });
    };
    const copyLink = () => navigator.clipboard.writeText(link)
        .then(() => message.success(mode === 'passcode' ? 'Guest link copied — share it with the passcode' : 'Guest link copied'))
        .catch(() => message.error('Could not copy the link'));

    const summary = (
        <Text type="secondary" style={{ fontSize: 'var(--fs-sm)' }}>
            {MODES[mode]?.icon} {MODES[mode]?.label}
            {guests?.active > 0 && ` · ${guests.active} ${guests.active === 1 ? 'guest' : 'guests'} here`}
        </Text>
    );

    const body = (
        <Flex vertical gap={10}>
            <Flex align="center" gap={12} wrap>
                <Tooltip title={disabled}>
                    <Segmented
                        value={mode}
                        onChange={chooseMode}
                        disabled={!!disabled}
                        options={Object.entries(MODES).map(([value, m]) => ({ value, label: m.label, icon: m.icon }))}
                    />
                </Tooltip>
            </Flex>
            <Text type="secondary" style={{ fontSize: 'var(--fs-sm)' }}>
                {MODES[mode]?.text} {mode !== 'off' && 'They only see the Gathering, everyone sees they’re a guest, and their pass lasts a day.'}
            </Text>
            {mode === 'public' && (
                <Alert type="warning" showIcon title="Whoever gets hold of the link can join — share it with care." />
            )}
            {mode === 'passcode' && (
                <Space.Compact style={{ width: '100%', maxWidth: 420 }}>
                    <Input
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        onBlur={commitPasscode}
                        onPressEnter={commitPasscode}
                        prefix={<LockOutlined style={{ color: 'var(--text-secondary)' }} />}
                        placeholder="Passcode"
                        aria-label="Guest passcode"
                        maxLength={64}
                        disabled={!!disabled}
                    />
                    <Tooltip title="Make a new passcode — the old one stops working">
                        <Button icon={<ThunderboltOutlined />} onClick={() => onChange({ guest_passcode: generatePasscode() })} disabled={!!disabled}>
                            New
                        </Button>
                    </Tooltip>
                </Space.Compact>
            )}
            {guests && (guests.active > 0 || guests.expired > 0) && (
                <Flex align="center" justify="space-between" gap={8} wrap>
                    <Text type="secondary" style={{ fontSize: 'var(--fs-sm)' }}>
                        {guests.active} {guests.active === 1 ? 'guest' : 'guests'} here
                        {guests.expired > 0 && ` · ${guests.expired} expired, not yet removed`}
                    </Text>
                    <Space size={6} wrap>
                        {guests.expired > 0 && (
                            <Button size="small" icon={<ClearOutlined />} onClick={() => onRemoveGuests('expired')}>
                                Remove expired
                            </Button>
                        )}
                        {guests.active > 0 && (
                            <Popconfirm
                                title="Remove every guest?"
                                description="Ends every guest pass now — their picks and room seats go too. Guests on the page are sent back to the join page."
                                okText="Remove all guests"
                                okButtonProps={{ danger: true }}
                                onConfirm={() => onRemoveGuests('all')}
                            >
                                <Button size="small" danger icon={<UserDeleteOutlined />}>Remove all guests</Button>
                            </Popconfirm>
                        )}
                    </Space>
                </Flex>
            )}
            {mode !== 'off' && (
                <Flex align="center" gap={8} wrap>
                    <Text type="secondary" className="gathering-guest-link">{link}</Text>
                    <Button size="small" icon={<CopyOutlined />} onClick={copyLink} disabled={!!disabled}>
                        Copy guest link
                    </Button>
                </Flex>
            )}
        </Flex>
    );

    return (
        <Collapse
            size="small"
            className="gathering-section"
            items={[{ key: 'guests', label: <Text strong>Guests</Text>, extra: summary, children: body }]}
        />
    );
}

export default GuestAccess;
